import { PageSize } from '@/constant';
import {
	GetWorkerAssignmentApplicantListParams,
	GetWorkerAssignmentListParams,
	ICommonApplicant,
	IWorkerAssignmentApplicantItem,
	IWorkerAssignmentEvaluation,
	IWorkerAssignmentItem,
	IWorkerAssignmentListItem,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
	formatWorkerAssignmentApplicantList,
	formatWorkerAssignmentDetail,
	formatWorkerAssignmentList,
	RootState,
} from '..';
import {
	IPayloadDeleteWorkerAssignmentRequest,
	IPayloadGetWorkerAssignmentApplicantListRequest,
	IPayloadGetWorkerAssignmentApplicantListSuccess,
	IPayloadGetWorkerAssignmentDetailRequest,
	IPayloadGetWorkerAssignmentDetailSuccess,
	IPayloadGetWorkerAssignmentEvaluationRequest,
	IPayloadGetWorkerAssignmentEvaluationSuccess,
	IPayloadGetWorkerAssignmentExcelListRequest,
	IPayloadGetWorkerAssignmentListRequest,
	IPayloadGetWorkerAssignmentListSuccess,
	IPayloadWorkerAssignmentApplicantStatusRequest,
	IWorkerAssignmentApplicantDetailStore,
	IWorkerAssignmentDetailStore,
	IWorkerAssignmentListStore,
	IWorkerAssignmentUploadAssignmentFailedDetailStore,
	IWorkerAssignmentUploadPublicAssignmentFailedDetailStore,
	TPayloadUploadExcelAssignmentRequest,
	TPayloadUploadExcelAssignmentSuccess,
	TPayloadUploadExcelPublicAssignmentRequest,
	TPayloadUploadExcelPublicAssignmentSuccess,
} from './worker-assignment.type';

const InitParams: GetWorkerAssignmentListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const InitAssignmentApplicantParams: GetWorkerAssignmentApplicantListParams = {
	id: 0,
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	excelListFetching: false,
	workerAssignmentList: [] as IWorkerAssignmentListStore[],
	total: 0,
	params: InitParams,
	workerAssignmentDetail: {} as IWorkerAssignmentDetailStore,

	workerAssignmentApplicantFetching: false,
	workerAssignmentApplicantList: [] as IWorkerAssignmentApplicantDetailStore[],
	workerAssignmentApplicantTotal: 0,
	workerAssignmentApplicantParams: InitAssignmentApplicantParams,

	evaluation: {} as IWorkerAssignmentEvaluation,

	uploadExcelAssignmentFailedList:
		[] as IWorkerAssignmentUploadAssignmentFailedDetailStore[],
	uploadExcelAssignmentResult: {
		successCount: 0,
		failCount: 0,
	} as {
		successCount: number;
		failCount: number;
	},

	uploadExcelPublicAssignmentFailedList:
		[] as IWorkerAssignmentUploadPublicAssignmentFailedDetailStore[],
	uploadExcelPublicAssignmentResult: {
		successCount: 0,
		failCount: 0,
	} as {
		successCount: number;
		failCount: number;
	},

	isUploadExcelProgramNameNotFound: false,
};

const workerAssignmentSlice = createSlice({
	initialState,
	name: 'workerAssignment',
	reducers: {
		/**---- GET WORKER ASSIGNMENT LIST START ---- */
		getWorkerAssignmentListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getWorkerAssignmentListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.workerAssignmentList = content;
			state.total = total;
		},
		getWorkerAssignmentListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.workerAssignmentList = [];
		},

		getWorkerAssignmentExcelListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentExcelListRequest>
		) => {
			state.excelListFetching = true;
		},
		getWorkerAssignmentExcelListSuccess: (state) => {
			state.excelListFetching = false;
		},
		getWorkerAssignmentExcelListFailure: (state) => {
			state.excelListFetching = false;
		},
		/**---- GET WORKER ASSIGNMENT LIST END ---- */

		/**---- DELETE WORKER ASSIGNMENT START ---- */
		deleteWorkerAssignmentRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerAssignmentRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerAssignmentSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerAssignmentFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER ASSIGNMENT START ---- */

		/**---- GET WORKER ASSIGNMENT DETAIL START ---- */
		getWorkerAssignmentDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerAssignmentDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentDetailSuccess>
		) => {
			const { workerAssignmentDetail } = action.payload;
			state.fetching = false;
			state.workerAssignmentDetail = workerAssignmentDetail;
		},
		getWorkerAssignmentDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET WORKER ASSIGNMENT DETAIL END ---- */

		/**---- UPDATE WORKER ASSIGNMENT START ---- */
		updateWorkerAssignmentListSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IWorkerAssignmentListItem>>
		) => {
			const { data } = action.payload;
			const workerAssignmentList = [...state.workerAssignmentList];
			state.workerAssignmentList = formatWorkerAssignmentList(
				mergeListItemUpdated<
					IWorkerAssignmentListStore,
					IWorkerAssignmentListItem
				>(workerAssignmentList, data)
			);
		},
		updateWorkerAssignmentDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IWorkerAssignmentItem>>
		) => {
			const { data } = action.payload;
			const workerAssignmentDetail = { ...state.workerAssignmentDetail };
			state.workerAssignmentDetail = formatWorkerAssignmentDetail(
				mergeItemUpdated(workerAssignmentDetail, data)
			);
		},
		updateWorkerAssignmentApplicantListSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<ICommonApplicant>>
		) => {
			const { data } = action.payload;
			const workerAssignmentApplicantList = [
				...state.workerAssignmentApplicantList,
			];
			state.workerAssignmentApplicantList = formatWorkerAssignmentApplicantList(
				mergeListItemUpdated<
					IWorkerAssignmentApplicantDetailStore,
					IWorkerAssignmentApplicantItem
				>(workerAssignmentApplicantList, data)
			);
		},
		/**---- UPDATE WORKER ASSIGNMENT END ---- */

		/**---- GET WORKER ASSIGNMENT APPLICANT LIST START ---- */
		getWorkerAssignmentApplicantListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentApplicantListRequest>
		) => {
			return {
				...state,
				workerAssignmentApplicantFetching: true,
				workerAssignmentApplicantParams: action?.payload,
			};
		},
		getWorkerAssignmentApplicantListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentApplicantListSuccess>
		) => {
			const { content, total } = action.payload;
			state.workerAssignmentApplicantFetching = false;
			state.workerAssignmentApplicantList = content;
			state.workerAssignmentApplicantTotal = total;
		},
		getWorkerAssignmentApplicantListFailure: (state) => {
			state.workerAssignmentApplicantFetching = false;
			state.workerAssignmentApplicantTotal = 0;
			state.workerAssignmentApplicantList = [];
		},
		/**---- GET WORKER ASSIGNMENT APPLICANT LIST END ---- */

		/**---- UPDATE WORKER ASSIGNMENT APPLICANT STATUS START ---- */
		updateWorkerAssignmentApplicantStatusRequest: (
			state,
			action: PayloadAction<IPayloadWorkerAssignmentApplicantStatusRequest>
		) => {
			state.fetching = true;
		},
		updateWorkerAssignmentApplicantStatusSuccess: (state) => {
			state.fetching = false;
		},
		updateWorkerAssignmentApplicantStatusFailure: (state) => {
			state.fetching = false;
		},
		/**---- UPDATE WORKER ASSIGNMENT APPLICANT STATUS END ---- */

		/**---- GET WORKER ASSIGNMENT EVALUATION START ---- */
		getWorkerAssignmentEvaluationRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentEvaluationRequest>
		) => {
			state.fetching = true;
		},
		getWorkerAssignmentEvaluationSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentEvaluationSuccess>
		) => {
			const { evaluation } = action.payload;
			state.fetching = false;
			state.evaluation = evaluation;
		},
		getWorkerAssignmentEvaluationFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET WORKER ASSIGNMENT EVALUATION END ---- */

		/**---- UPLOAD EXCEL WORKER ASSIGNMENT START ---- */
		uploadExcelAssignmentRequest: (
			state,
			action: PayloadAction<TPayloadUploadExcelAssignmentRequest>
		) => {
			state.fetching = true;

			state.uploadExcelAssignmentFailedList = [];
			state.uploadExcelAssignmentResult = {
				successCount: 0,
				failCount: 0,
			};
		},
		uploadExcelAssignmentSuccess: (
			state,
			action: PayloadAction<TPayloadUploadExcelAssignmentSuccess>
		) => {
			state.fetching = false;

			state.uploadExcelAssignmentFailedList = action.payload.failedList;
			state.uploadExcelAssignmentResult = {
				successCount: action.payload.successCount || 0,
				failCount: action.payload.failCount || 0,
			};
			state.isUploadExcelProgramNameNotFound =
				action.payload.isUploadExcelProgramNameNotFound || false;
		},
		uploadExcelAssignmentFailure: (state) => {
			state.fetching = false;
			state.uploadExcelAssignmentResult = {
				successCount: 0,
				failCount: 0,
			};
		},

		uploadExcelPublicAssignmentRequest: (
			state,
			action: PayloadAction<TPayloadUploadExcelPublicAssignmentRequest>
		) => {
			state.fetching = true;

			state.uploadExcelPublicAssignmentFailedList = [];
			state.uploadExcelPublicAssignmentResult = {
				successCount: 0,
				failCount: 0,
			};
		},
		uploadExcelPublicAssignmentSuccess: (
			state,
			action: PayloadAction<TPayloadUploadExcelPublicAssignmentSuccess>
		) => {
			state.fetching = false;
			state.uploadExcelPublicAssignmentFailedList = action.payload.failedList;
			state.uploadExcelPublicAssignmentResult = {
				successCount: action.payload.successCount || 0,
				failCount: action.payload.failCount || 0,
			};
			state.isUploadExcelProgramNameNotFound =
				action.payload.isUploadExcelProgramNameNotFound || false;
		},
		uploadExcelPublicAssignmentFailure: (state) => {
			state.fetching = false;
			state.uploadExcelPublicAssignmentResult = {
				successCount: 0,
				failCount: 0,
			};
		},
		/**---- UPLOAD EXCEL WORKER ASSIGNMENT END ---- */
	},
});

export const WorkerAssignmentSelectors = {
	selectFetching: (state: RootState) => state.workerAssignment.fetching,
	selectParams: (state: RootState) => state.workerAssignment.params,
	selectList: (state: RootState) => state.workerAssignment.workerAssignmentList,
	selectTotal: (state: RootState) => state.workerAssignment.total,
	selectDetail: (state: RootState) =>
		state.workerAssignment.workerAssignmentDetail,

	selectAssignmentApplicantFetching: (state: RootState) =>
		state.workerAssignment.workerAssignmentApplicantFetching,
	selectAssignmentApplicantParams: (state: RootState) =>
		state.workerAssignment.workerAssignmentApplicantParams,
	selectAssignmentApplicantList: (state: RootState) =>
		state.workerAssignment.workerAssignmentApplicantList,
	selectAssignmentApplicantTotal: (state: RootState) =>
		state.workerAssignment.workerAssignmentApplicantTotal,

	selectEvaluation: (state: RootState) => state.workerAssignment.evaluation,

	selectUploadExcelAssignmentFailedList: (state: RootState) =>
		state.workerAssignment.uploadExcelAssignmentFailedList,
	selectUploadExcelAssignmentResult: (state: RootState) =>
		state.workerAssignment.uploadExcelAssignmentResult,
	selectUploadExcelPublicAssignmentFailedList: (state: RootState) =>
		state.workerAssignment.uploadExcelPublicAssignmentFailedList,
	selectUploadExcelPublicAssignmentResult: (state: RootState) =>
		state.workerAssignment.uploadExcelPublicAssignmentResult,
	selectIsUploadExcelProgramNameNotFound: (state: RootState) =>
		state.workerAssignment.isUploadExcelProgramNameNotFound,
};

export const WorkerAssignmentActions = workerAssignmentSlice.actions;
export const WorkerAssignmentReducer = workerAssignmentSlice.reducer;
